// Top-right user menu for the admin SPA — dropdown with the signed-in user's
// email, a link to Settings → Security, and Sign out.
//
// Extracted from AdminNav so each component stays small and the menu's local
// state (open/closed, user lookup, click-outside listener) lives on the
// concern that owns it.

import { useEffect, useRef, useState } from "react";
import type { JSX } from "react";
import { Link, useNavigate } from "react-router-dom";
import { adminFetch, setAccessToken } from "../lib/api.js";

interface CurrentUser {
  email?: string;
  full_name?: string;
}

export function UserMenu(): JSX.Element {
  const navigate = useNavigate();
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Pull the current admin's email for the menu trigger. Failure here is
  // silent — the menu still works, it just shows "Account" instead of the
  // address. The Security/Sign-out items don't depend on this.
  useEffect(() => {
    void (async () => {
      try {
        const me = await adminFetch<CurrentUser>("/admin/users/me");
        setUser({ email: me.email, full_name: me.full_name });
      } catch { /* keep fallback label */ }
    })();
  }, []);

  // Close on click-outside and on Escape. Listeners are only attached while
  // the menu is open so we don't pay event-loop cost on every nav render.
  useEffect(() => {
    if (!open) return;
    function onPointer(e: MouseEvent): void {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent): void {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  async function signOut(): Promise<void> {
    // Server-side: clear the admin_session httpOnly cookie. Best-effort —
    // even if the request fails, the access path below still expires the
    // cached access token and routes the user to login.
    try { await adminFetch<void>("/auth/logout", { method: "POST" }); } catch { /* ignore */ }
    setAccessToken(null);
    setOpen(false);
    void navigate("/login", { replace: true });
  }

  const triggerLabel = user?.email ?? "Account";

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-indigo-200 hover:bg-indigo-600 hover:text-white transition-colors"
      >
        <span className="max-w-[14rem] truncate">{triggerLabel}</span>
        <span aria-hidden="true" className={`transition-transform ${open ? "rotate-180" : ""}`}>▾</span>
      </button>

      {open && (
        <div
          role="menu"
          aria-label="Account menu"
          className="absolute right-0 mt-2 w-56 rounded-lg bg-white text-slate-800 shadow-lg ring-1 ring-black/5 z-50 overflow-hidden"
        >
          {user?.email && (
            <div className="px-4 py-3 border-b border-slate-200">
              <p className="text-xs text-slate-500">Signed in as</p>
              <p className="text-sm font-medium truncate" title={user.email}>{user.email}</p>
            </div>
          )}
          <Link
            to="/settings/security"
            role="menuitem"
            onClick={() => setOpen(false)}
            className="block px-4 py-2 text-sm hover:bg-slate-100 focus:bg-slate-100 focus:outline-none"
          >
            Security
          </Link>
          <button
            type="button"
            role="menuitem"
            onClick={() => { void signOut(); }}
            className="block w-full text-left px-4 py-2 text-sm border-t border-slate-200 hover:bg-slate-100 focus:bg-slate-100 focus:outline-none"
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}
